import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Timer, Bitcoin } from "lucide-react";

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!targetDate) return;
    const calc = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

export default function HalvingCountdown() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/tools/halving-countdown")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const timeLeft = useCountdown(data?.estimated_date);

  const Box = ({ value, label }) => (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 text-center">
      <div className="font-display font-bold text-4xl md:text-5xl text-yellow-400">{String(value).padStart(2, "0")}</div>
      <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widest mt-2">{label}</div>
    </div>
  );

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Bitcoin className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Bitcoin Halving Countdown</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          Time remaining until Bitcoin's next block reward halving.
        </p>
      </div>

      {error ? (
        <p className="text-center text-red-400 font-mono text-sm">Could not load halving data. Try again shortly.</p>
      ) : !data || !timeLeft ? (
        <p className="text-center text-[var(--text-tertiary)] font-mono text-sm">Loading current block height...</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-8">
            <Box value={timeLeft.days} label="Days" />
            <Box value={timeLeft.hours} label="Hours" />
            <Box value={timeLeft.minutes} label="Minutes" />
            <Box value={timeLeft.seconds} label="Seconds" />
          </div>

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Halving #</div>
              <div className="font-bold text-[var(--text-primary)] mt-1">{data.halving_number}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Current Block</div>
              <div className="font-bold text-[var(--text-primary)] mt-1">{data.current_block.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Blocks Left</div>
              <div className="font-bold text-[var(--text-primary)] mt-1">{data.blocks_remaining.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Reward Change</div>
              <div className="font-bold text-[var(--text-primary)] mt-1">{data.current_reward} → {data.next_reward} BTC</div>
            </div>
          </div>
        </>
      )}

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How the Halving Countdown works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Track block height</div>
            <p className="text-xs text-[var(--text-secondary)]">Bitcoin halves its mining reward every 210,000 blocks. The tool checks the current confirmed block height in real time.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Estimate the date</div>
            <p className="text-xs text-[var(--text-secondary)]">Using Bitcoin's average 10 minute block time, the remaining blocks are converted into an estimated calendar date.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Show the reward change</div>
            <p className="text-xs text-[var(--text-secondary)]">Every halving cuts the block reward in half, directly affecting new Bitcoin supply entering circulation.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          Bitcoin's issuance schedule is fixed by protocol rules, not by any company or authority. Every 210,000 blocks, roughly every four years, the reward paid to miners for confirming a new block is cut in half. This controls inflation and is a core part of Bitcoin's scarcity model, since the total supply is capped at 21 million coins.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          The countdown is an estimate based on average block times, which can vary due to network hash rate changes. The actual halving date may shift by a few days in either direction as real block production speeds up or slows down. This tool is informational only and is not financial advice.
        </p>
      </div>
    </div>
  );
}